// BLF tile colours + the legacy contrast algorithm, ported verbatim from the WinForms
// DssButtonUserControl (ProvideContrastingColor / ChangeColorBrightness). The "specific
// colours" are the legacy ones; idle uses the theme surface (handled in the panel).
import type { ExtensionState } from '../contract'

// Legacy DssButtonUserControl back-colours.
export const BLF = {
  inUse: '#C43232',   // Color.FromArgb(196,50,50)
  onHold: '#E6E182',  // legacy #FFFF66 desaturated a touch
  dnd: '#FFC000',     // Color.FromArgb(255,192,0)
  offline: '#A9A9A9', // Color.DarkGray
  idle: '#D3D3D3'     // Color.LightGray (legacy IdleBackColor) — used for presence dots
} as const

interface RGB { r: number; g: number; b: number }
const parse = (hex: string): RGB => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16)
})
const toHex = (r: number, g: number, b: number): string =>
  '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')

// Public Shared Function ChangeColorBrightness(color, correctionFactor) — verbatim.
export function changeColorBrightness(hex: string, factor: number): string {
  let { r, g, b } = parse(hex)
  if (factor < 0) {
    const c = 1 + factor
    r *= c; g *= c; b *= c
  } else {
    r = (255 - r) * factor + r
    g = (255 - g) * factor + g
    b = (255 - b) * factor + b
  }
  return toHex(r, g, b)
}

// Private Function ProvideContrastingColor(colorToUse) — verbatim weighting + thresholds.
export function contrastingColor(hex: string): string {
  const { r, g, b } = parse(hex)
  const finalValue = Math.round(Math.sqrt(r * r * 0.249 + g * g * 0.587 + b * b * 0.164))
  return finalValue > 140 ? changeColorBrightness(hex, -0.75) : changeColorBrightness(hex, 0.85)
}

/** Tile background for a state. null = use the theme surface (idle, not in DND). Ringing
 *  keeps the theme surface and adds the blinking border in the panel. */
export function tileBackground(state: ExtensionState, isDnd: boolean): string | null {
  switch (state) {
    case 'in_use':
    case 'busy': return BLF.inUse
    case 'on_hold': return BLF.onHold
    case 'unavailable':
    case 'invalid': return BLF.offline
    case 'not_in_use': return isDnd ? BLF.dnd : null
    case 'ringing': return isDnd ? BLF.dnd : null
    default: return null
  }
}

/** Presence-dot colour for a contact's extension (Contacts view). Same scheme as the BLF
 *  tiles; idle shows the light-gray "available" dot. Ringing is handled by the blink class. */
export function dotColor(state: ExtensionState, isDnd: boolean): string {
  switch (state) {
    case 'in_use':
    case 'busy': return BLF.inUse
    case 'on_hold': return BLF.onHold
    case 'unavailable':
    case 'invalid': return BLF.offline
    case 'ringing': return BLF.idle
    case 'not_in_use': return isDnd ? BLF.dnd : BLF.idle
    default: return BLF.offline
  }
}
